import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import './AdminPages.css';

const CarVariants = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [variants, setVariants] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [formData, setFormData] = useState({
    model: '',
    name: '',
    slug: '',
    variant_type: '',
    is_active: true,
    image: null,
    image_front: null,
    image_rear: null,
    image_side: null,
    image_interior: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewRear, setImagePreviewRear] = useState(null);
  const [imagePreviewSide, setImagePreviewSide] = useState(null);
  const [imagePreviewInterior, setImagePreviewInterior] = useState(null);
  const [reorderSaving, setReorderSaving] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchVariants();
    fetchModels();
  }, []);

  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openAddForm, location.pathname, navigate]);

  const fetchVariants = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCarVariants();
      const data = response.data;
      let variantsData = [];
      if (Array.isArray(data)) {
        variantsData = data;
      } else if (data && data.results && Array.isArray(data.results)) {
        variantsData = data.results;
      }
      variantsData.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setVariants(variantsData);
    } catch (err) {
      console.error('Error fetching variants:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || 'فشل تحميل السيارات';
      alert(errorMessage);
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await adminAPI.getCarModels();
      const data = response.data;
      if (Array.isArray(data)) {
        setModels(data);
      } else if (data && data.results && Array.isArray(data.results)) {
        setModels(data.results);
      } else {
        setModels([]);
      }
    } catch (err) {
      console.error('Error fetching models:', err);
      setModels([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0] || null;
    setFormData(prev => ({
      ...prev,
      [name]: file
    }));
    
    // معاينة لأي صورة مختارة
    if ((name === 'image' || name === 'image_front') && file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else if ((name === 'image' || name === 'image_front') && !file) setImagePreview(null);
    if (name === 'image_rear' && file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewRear(reader.result);
      reader.readAsDataURL(file);
    } else if (name === 'image_rear' && !file) setImagePreviewRear(null);
    if (name === 'image_side' && file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewSide(reader.result);
      reader.readAsDataURL(file);
    } else if (name === 'image_side' && !file) setImagePreviewSide(null);
    if (name === 'image_interior' && file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewInterior(reader.result);
      reader.readAsDataURL(file);
    } else if (name === 'image_interior' && !file) setImagePreviewInterior(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('model', formData.model);
      data.append('name', formData.name);
      if (formData.slug && formData.slug.trim()) {
        data.append('slug', formData.slug.trim());
      }
      data.append('variant_type', formData.variant_type);
      data.append('is_active', formData.is_active);
      if (formData.image) data.append('image', formData.image);
      if (formData.image_front) data.append('image_front', formData.image_front);
      if (formData.image_rear) data.append('image_rear', formData.image_rear);
      if (formData.image_side) data.append('image_side', formData.image_side);
      if (formData.image_interior) data.append('image_interior', formData.image_interior);

      if (editingVariant) {
        const response = await adminAPI.updateCarVariant(editingVariant.id, data);
        console.log('Update response:', response.data);
        alert('تم تحديث السيارة بنجاح');
      } else {
        const response = await adminAPI.createCarVariant(data);
        console.log('Create response:', response.data);
        alert('تم إضافة السيارة بنجاح');
      }

      resetForm();
      // إعادة جلب البيانات بعد الحفظ
      await fetchVariants();
    } catch (err) {
      console.error('Error saving variant:', err);
      console.error('Error details:', err.response?.data);
      alert('فشل حفظ السيارة: ' + (err.response?.data?.error || err.message || 'خطأ غير معروف'));
    }
  };

  const handleEdit = (variant) => {
    setEditingVariant(variant);
    setFormData({
      model: variant.model || '',
      name: variant.name || '',
      slug: variant.slug || '',
      variant_type: variant.variant_type || '',
      is_active: variant.is_active !== undefined ? variant.is_active : true,
      image: null,
      image_front: null,
      image_rear: null,
      image_side: null,
      image_interior: null,
    });
    setImagePreview(variant.image_url || null);
    setImagePreviewRear(null);
    setImagePreviewSide(null);
    setImagePreviewInterior(null);
    setShowForm(true);
  };

  const handleMove = async (index, direction) => {
    if (direction === 'up' && index <= 0) return;
    if (direction === 'down' && index >= variants.length - 1) return;
    const newList = [...variants];
    const swap = direction === 'up' ? index - 1 : index + 1;
    [newList[index], newList[swap]] = [newList[swap], newList[index]];
    const variantIds = newList.map((v) => v.id).filter((id) => id != null);
    if (variantIds.length === 0) return;
    setVariants(newList);
    setReorderSaving(true);
    try {
      await adminAPI.reorderCarVariants(variantIds);
    } catch (err) {
      setVariants(variants);
      console.error('Reorder error:', err);
      alert(err.response?.data?.error || err.response?.data?.detail || err.message || 'فشل تحديث الترتيب');
    } finally {
      setReorderSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه السيارة؟')) return;
    
    try {
      await adminAPI.deleteCarVariant(id);
      alert('تم حذف السيارة بنجاح');
      fetchVariants();
    } catch (err) {
      console.error('Error deleting variant:', err);
      alert('فشل حذف السيارة');
    }
  };

  const resetForm = () => {
    setFormData({
      model: '',
      name: '',
      slug: '',
      variant_type: '',
      is_active: true,
      image: null,
      image_front: null,
      image_rear: null,
      image_side: null,
      image_interior: null,
    });
    setImagePreview(null);
    setImagePreviewRear(null);
    setImagePreviewSide(null);
    setImagePreviewInterior(null);
    setEditingVariant(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>إدارة السيارات</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          إضافة سيارة جديدة
        </button>
      </div>

      {showForm && (
        <div className="admin-modal-backdrop" onClick={resetForm}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingVariant ? 'تعديل السيارة' : 'إضافة سيارة جديدة'}</h2>
              <button type="button" className="admin-modal-close" onClick={resetForm} aria-label="إغلاق">×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-container">
                <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label>الموديل *</label>
              <select
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
              >
                <option value="">اختر الموديل</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}{model.year_range ? ` (${model.year_range})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>اسم السيارة *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>المسار في الرابط (slug)</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                placeholder="يُولَّد تلقائياً إن تُرك فارغاً"
                dir="ltr"
              />
            </div>

            <div className="form-group">
              <label>نوع الطراز</label>
              <select
                name="variant_type"
                value={formData.variant_type}
                onChange={handleInputChange}
              >
                <option value="">اختر النوع</option>
                <option value="sedan">سيدان</option>
                <option value="suv">SUV</option>
                <option value="coupe">كوبيه</option>
                <option value="wagon">واغن</option>
                <option value="convertible">كابريوليه</option>
                <option value="hatchback">هاتشباك</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                نشط
              </label>
            </div>

            <div className="form-group">
              <label>صورة أمامية</label>
              <input type="file" name="image_front" accept="image/*" onChange={handleFileChange} />
              {(imagePreview && formData.image_front) || (editingVariant && (editingVariant.image_front_url || editingVariant.image_url)) ? (
                <div className="current-image" style={{ marginTop: '10px' }}>
                  <img src={imagePreview || editingVariant.image_front_url || editingVariant.image_url} alt="أمامية" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              ) : null}
            </div>
            <div className="form-group">
              <label>صورة خلفية</label>
              <input type="file" name="image_rear" accept="image/*" onChange={handleFileChange} />
              {((imagePreviewRear && formData.image_rear) || (editingVariant && editingVariant.image_rear_url)) ? (
                <div className="current-image" style={{ marginTop: '10px' }}>
                  <img src={imagePreviewRear || editingVariant?.image_rear_url} alt="خلفية" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              ) : null}
            </div>
            <div className="form-group">
              <label>صورة جانبية</label>
              <input type="file" name="image_side" accept="image/*" onChange={handleFileChange} />
              {((imagePreviewSide && formData.image_side) || (editingVariant && editingVariant.image_side_url)) ? (
                <div className="current-image" style={{ marginTop: '10px' }}>
                  <img src={imagePreviewSide || editingVariant?.image_side_url} alt="جانبية" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              ) : null}
            </div>
            <div className="form-group">
              <label>صورة داخلية</label>
              <input type="file" name="image_interior" accept="image/*" onChange={handleFileChange} />
              {((imagePreviewInterior && formData.image_interior) || (editingVariant && editingVariant.image_interior_url)) ? (
                <div className="current-image" style={{ marginTop: '10px' }}>
                  <img src={imagePreviewInterior || editingVariant?.image_interior_url} alt="داخلية" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              ) : null}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingVariant ? 'تحديث' : 'إضافة'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                إلغاء
              </button>
            </div>
          </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>الترتيب</th>
              <th>الصورة</th>
              <th>الموديل</th>
              <th>الاسم</th>
              <th>طراز السيارة</th>
              <th>عدد قطع الغيار</th>
              <th>الحالة</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {variants.length === 0 ? (
              <tr className="empty-row">
                <td colSpan="8">لا توجد سيارات</td>
              </tr>
            ) : (
              variants.map((variant, index) => (
                <tr key={variant.id}>
                  <td data-label="الترتيب" className="td-reorder">
                    <button type="button" className="btn btn-sm btn-icon btn-reorder" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMove(index, 'up'); }} disabled={index === 0 || reorderSaving} title="تحريك لأعلى" aria-label="تحريك لأعلى">
                      <i className="fas fa-chevron-up" />
                    </button>
                    <button type="button" className="btn btn-sm btn-icon btn-reorder" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMove(index, 'down'); }} disabled={index === variants.length - 1 || reorderSaving} title="تحريك لأسفل" aria-label="تحريك لأسفل">
                      <i className="fas fa-chevron-down" />
                    </button>
                    {reorderSaving && <span className="reorder-saving-label">حفظ...</span>}
                  </td>
                  <td data-label="الصورة">
                    {(variant.image_front_url || variant.image_url) ? (
                      <img 
                        src={variant.image_front_url || variant.image_url} 
                        alt={variant.name} 
                        className="table-image table-image--large"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling?.remove();
                          const span = document.createElement('span');
                          span.className = 'no-image';
                          span.textContent = 'خطأ في تحميل الصورة';
                          e.target.parentNode.appendChild(span);
                        }}
                      />
                    ) : (
                      <span className="no-image">—</span>
                    )}
                  </td>
                  <td data-label="الموديل">{variant.model_name || '—'}</td>
                  <td data-label="الاسم">{variant.name}</td>
                  <td data-label="طراز السيارة">{variant.variant_type || '—'}</td>
                  <td data-label="عدد قطع الغيار">{variant.parts_count || 0}</td>
                  <td data-label="الحالة">
                    <span className={`status-badge ${variant.is_active ? 'active' : 'inactive'}`}>
                      {variant.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                  </td>
                  <td data-label="الإجراءات">
                    <div className="td-actions">
                      <button type="button" className="btn btn-sm btn-icon btn-edit" onClick={() => handleEdit(variant)} title="تعديل" aria-label="تعديل">
                        <i className="fas fa-pen" />
                      </button>
                      <button type="button" className="btn btn-sm btn-icon btn-delete" onClick={() => handleDelete(variant.id)} title="حذف" aria-label="حذف">
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarVariants;
